import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ServicesGrid from "@/components/ServicesGrid";
import PoolSystemsPromo from "@/components/PoolSystemsPromo";
import Reveal from "@/components/SectionReveal";
import { useSeo, localBusinessJsonLd } from "@/lib/seo";
import { SITE, SERVICES, CITIES } from "@/data/site";

export default function ServicesPage() {
  useSeo({
    title: "Pool Cleaning Services in Tampa Bay, FL",
    description:
      "Full pool services in Tampa Bay — pool cleaning, weekly pool service, pool maintenance, pool filter cleaning, pool pump cleaning, green pool cleanup, chemical balancing, equipment repair, and 24/7 pool emergency. Serving Clearwater, Palm Harbor, Dunedin, Tarpon Springs, Trinity, Oldsmar, Westchase, Town & Country, Odessa, and Carrollwood.",
    path: "/services",
    image: SITE.logo,
    jsonLd: localBusinessJsonLd(),
  });

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <main className="bg-white">
      {/* Header */}
      <section className="pt-32 sm:pt-44 pb-12 bg-gradient-to-br from-sky-50 via-white to-amber-50/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">
              Tampa Bay Pool Pros
            </p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 max-w-4xl">
              Pool services that keep your water crystal clear.
            </h1>
            <p className="mt-5 max-w-2xl text-slate-700 text-lg">
              Weekly pool cleaning, full pool maintenance, filter &amp; pump service, green pool cleanup,
              and 24/7 pool emergency response. Choose a service to learn more.
            </p>
          </Reveal>
        </div>
      </section>

      <ServicesGrid />
      <PoolSystemsPromo />

      {/* Per-service link grid */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal className="max-w-3xl">
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">
              Browse Each Service
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-slate-900">
              Detailed pages for every pool service we offer.
            </h2>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s, i) => (
              <Reveal
                key={s.slug}
                delay={i * 0.03}
                direction={i % 2 === 0 ? "up" : "left"}
                testId={`browse-${s.slug}`}
              >
                <Link
                  to={`/services/${s.slug}`}
                  className="block rounded-3xl bg-white border border-slate-100 hover:border-sky-300 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-all p-6"
                >
                  <h3 className="font-heading font-bold text-lg text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.blurb}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-sky-700">
                    Learn more <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Service areas footer */}
      <section className="py-14 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 text-center">
          <p className="text-xs tracking-[0.22em] uppercase text-amber-400 font-bold">
            All Services Available In
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/service-areas/${c.slug}`}
                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
